/**
 * Help content — the in-product mirror of docs/SpectraIQ_Twilio_Setup_Guide.md
 * and docs/SpectraIQ_Dealer_Onboarding_Guide.md.
 *
 * Kept as structured data rather than a markdown blob so the Help page can
 * search it, deep-link into a single step, and render callouts properly. If you
 * change the docs, change this too — they're meant to say the same thing.
 *
 * Shape:
 *   article  = { id, title, blurb, minutes, audience, category, sections[] }
 *   section  = { id, heading, blocks[] }
 *   block    = { type: 'p'|'steps'|'note'|'warn'|'table'|'code'|'list', ... }
 *
 * `category` groups the articles in the Help sidebar. Order of the array is the
 * order shown; the first article is what opens by default, so the orientation
 * guide comes first.
 */

export const ARTICLES = [
  /* ═══════════════════════ GET STARTED ═══════════════════════ */
  {
    id: 'getting-started',
    title: 'Getting around SpectraIQ',
    blurb: 'The five workspaces, how the AI runs your conversations, and the one prompt that tells you what to do next.',
    minutes: '5 min read',
    audience: 'Everyone',
    category: 'Get started',
    sections: [
      {
        id: 'gs-workspaces',
        heading: 'The five workspaces',
        blocks: [
          { type: 'p', text:
            'SpectraIQ is organised into five workspaces. Switch between them from the app switcher at the top of the ' +
            'left sidebar — each one changes the navigation underneath it to just the screens for that job.' },
          { type: 'table', head: ['Workspace', 'What lives there'], rows: [
            ['**Sales**', 'Dashboard, Lead Accelerator, Deals, Deal Desk (quotes), Customers, Appointments, Tasks'],
            ['**Showroom**', 'Test Drives, Loan Cars, Appraisals, Inventory'],
            ['**Marketing**', 'Campaigns, Surveys, Lost Leads'],
            ['**Insights**', 'Analytics, Pilot KPIs, Compliance'],
            ['**Platform**', 'Settings, Team, Onboarding, Plan & Billing, Dealer Health, Triage & QA, Pipeline Settings, Help'],
          ]},
          { type: 'note', text:
            'You only see what your role allows. A Sales Consultant lives mostly in Sales; a Dealer Principal sees ' +
            'everything, including Platform.' },
        ],
      },
      {
        id: 'gs-ai',
        heading: 'The AI answers first — you can always take over',
        blocks: [
          { type: 'p', text:
            'Every inbound message and call is answered by the AI assistant by default: it replies on SMS, WhatsApp and ' +
            'email, and can answer the phone in its own voice. Everything it does lands in the same screens your team uses, ' +
            'so a conversation the AI started is one you can pick up mid-thread.' },
          { type: 'list', items: [
            'On any lead, click **Take over from AI** to pause automation and reply yourself.',
            'Click **Hand back to AI** to let it resume.',
            'When the AI wants a human — an escalation, a complaint, a low-confidence moment — it **flags** the lead.',
          ]},
          { type: 'note', title: 'Where flagged leads show up', text:
            'Flagged cards carry a pulse dot on the Lead Accelerator board, and the **🔔 Needs attention** filter shows ' +
            'only those leads. Opening a flagged lead clears its alert.' },
        ],
      },
      {
        id: 'gs-next-action',
        heading: 'Next best action',
        blocks: [
          { type: 'p', text:
            'Leads, deals and customer profiles all show a **Next best action** — a single, computed “do this next” prompt ' +
            '(assign a consultant, create a quote, book an appointment, follow up). It reads the record’s stage, owner, ' +
            'quotes and appointments, so it’s the fastest way to know what a record needs without reading its whole history.' },
        ],
      },
      {
        id: 'gs-lead-deal',
        heading: 'A lead and a deal are the same record',
        blocks: [
          { type: 'p', text:
            'A “deal” is just a lead that has reached a money stage. The **conversation** always lives on the lead; the ' +
            '**Deal** view is the commercial side (value, quote, finance, close plan) of that same record. Jump between ' +
            'them with the **💬 Conversation** and **Deals** links — you’re never duplicating anything.' },
          { type: 'note', text:
            'This is why closing works the way it does: you build the quote in the Deal Desk, and completing the sale ' +
            'advances the very same lead that started as an inbound text.' },
        ],
      },
    ],
  },

  /* ═══════════════════════ DAILY USE ═══════════════════════ */
  {
    id: 'lead-accelerator',
    title: 'Work leads in the Lead Accelerator',
    blurb: 'The sales pipeline — every enquiry as a card, moved through the journey, with the AI doing the first response.',
    minutes: '8 min read',
    audience: 'Consultants · BDC · Managers',
    category: 'Daily use',
    sections: [
      {
        id: 'la-board',
        heading: 'Board and table views',
        blocks: [
          { type: 'p', text:
            'Open **Sales → Lead Accelerator**. The board shows ten stage columns — Inbound, Contacted, Qualified, ' +
            'Test drive, Negotiation, Quoted, Booked, Retailed, Delivered, Lost — each with a card count and the summed ' +
            'value of its cards. Switch to **Table** in the top bar for a sortable list (Customer, Vehicle, Stage, Score, ' +
            'Source, Last activity, Created).' },
          { type: 'p', text:
            'Each **card** shows the customer, vehicle of interest, a colour-coded **lead score** (hot ≥ 80, cold ≤ 40), ' +
            'the consultant owner (or a dashed **+** if unassigned), the preferred channel, an **⏱ SLA** chip if it’s ' +
            'breaching, and a pulse dot when the AI has flagged it.' },
        ],
      },
      {
        id: 'la-move',
        heading: 'Move a lead through the pipeline',
        blocks: [
          { type: 'steps', items: [
            'Drag a card into another column to change its stage — it saves immediately.',
            'Dropping onto **Lost** asks for a reason (required).',
            'To move a Delivered or Lost lead, open it and **Reopen** first — terminal columns are locked.',
          ]},
        ],
      },
      {
        id: 'la-filters',
        heading: 'Find the leads that need you',
        blocks: [
          { type: 'list', items: [
            '**Search** by name, phone, vehicle, notes, source or reference.',
            '**Stage**, **consultant** (incl. *Unassigned*) and **source** dropdowns narrow the board.',
            '**🔔 Needs attention** shows only leads the AI has flagged for a human.',
            'The counter shows matched/total, the hot count, and the live **SLA breach** count.',
          ]},
          { type: 'note', text:
            'An SLA breach is an early-stage lead (Inbound / Warm welcome / Discovery) left untouched for over 24 hours. ' +
            'The board refreshes itself every 15 seconds.' },
        ],
      },
      {
        id: 'la-assign',
        heading: 'Assign a consultant (or let the rules do it)',
        blocks: [
          { type: 'p', text:
            'On a lead, use the **assigned consultant** control to pick someone, or click **⚡ Auto** to apply your ' +
            'dealership’s assignment rules. Those rules are set in **Platform → Pipeline Settings** (round-robin, ' +
            'load-balanced, VIP, territory or source-based) — see “Team, roles, AI behaviour & routing”.' },
        ],
      },
      {
        id: 'la-detail',
        heading: 'Inside a lead',
        blocks: [
          { type: 'p', text:
            'Click any card to open the lead. The centre column is the **conversation**; type a reply and it goes out on ' +
            'the channel the customer last used (WhatsApp, SMS or email). The left column has the score, stage, owner, ' +
            'vehicle interest and a compact quotes panel; the right has appointments, actions and notes.' },
          { type: 'list', items: [
            '**Take over / Hand back** controls whether the AI is auto-replying.',
            '**→ next stage** advances the lead; **Change stage** opens the full picker.',
            '**📅 Book** creates an appointment; each appointment has its own next-step buttons.',
            '**💀 Mark as lost** moves it to the revival pool; a Lost lead shows **↺ Reopen**.',
          ]},
          { type: 'note', text:
            'The lead page updates live (it polls every few seconds), so an AI reply or a status change appears without ' +
            'a refresh. System notes like lost-lead revival stamps are hidden from the card and shown on the detail page.' },
        ],
      },
      {
        id: 'la-import',
        heading: 'Bulk-import leads',
        blocks: [
          { type: 'steps', items: [
            'Click **⭱ Import** on the board.',
            'Download the template, or map your own CSV/XLSX — columns match case- and spacing-insensitively.',
            'Upload; SpectraIQ reports created, updated and skipped rows, with a reason for every skip.',
          ]},
          { type: 'note', text: 'Phone is the only required column. Unknown columns are ignored, so a raw CRM export usually works.' },
        ],
      },
    ],
  },

  {
    id: 'deals-deal-desk',
    title: 'Deals & the Deal Desk',
    blurb: 'The commercial side — forecast, deal detail, and building a GST/LCT-aware quote that becomes a sale.',
    minutes: '8 min read',
    audience: 'Consultants · Business Managers · Sales Managers',
    category: 'Daily use',
    sections: [
      {
        id: 'dd-board',
        heading: 'The Deals board',
        blocks: [
          { type: 'p', text:
            'Open **Sales → Deals**. It’s the same pipeline filtered to money stages — Quoting, Negotiating, Committed, ' +
            'Won, Lost — with a forecast strip on top: open deals, open value, **weighted** (probability-adjusted) value ' +
            'and win rate. Close probability is set automatically by stage (Quoting 25%, Negotiating 55%, Committed 85%, ' +
            'Won 100%).' },
          { type: 'note', text:
            'A deal with no quoted drive-away price is estimated and clearly marked with a **~** — don’t read those as ' +
            'firm figures. A lead only appears here once it reaches Quoting.' },
        ],
      },
      {
        id: 'dd-detail',
        heading: 'The deal (money) view',
        blocks: [
          { type: 'p', text:
            'Opening a deal shows value, weighted value, age and score; a progress bar; the vehicle, trade-in and customer; ' +
            'an **indicative finance** calculator (deposit / term / balloon → weekly & monthly repayments); appointments; ' +
            'and a **Close plan** checklist. Each incomplete close-plan step has a shortcut button (Choose vehicle, Quote, ' +
            'Capture trade-in, Book delivery, Mark delivered).' },
          { type: 'p', text: 'Use **💬 Conversation** to jump back to the lead thread, or **Build a full quote →** to open the Deal Desk pre-filled.' },
        ],
      },
      {
        id: 'dd-quote',
        heading: 'Build a quote in the Deal Desk',
        blocks: [
          { type: 'steps', items: [
            'Open **Sales → Deal Desk** and click **+ New quote** (or **Build a full quote** from a deal).',
            'Set the customer, vehicle and the buyer’s **state** (drives on-road charges) and a valid-until date.',
            'Add **line items** — Vehicle, Accessory/option, Delivery, F&I product, Dealer fee, Government charge, Discount, Trade-in allowance, Other. Discounts and trade-ins auto-subtract; government charges are non-taxable.',
            'Toggle **Luxury Car Tax**, **Trade-in payout owing** and **Deposit** as needed.',
            'Watch the live **Summary**: drive-away price, of-which-GST, of-which-LCT, balance payable, and an estimated gross margin when a vehicle cost is known.',
          ]},
          { type: 'note', text:
            'Picking a vehicle auto-fills its line and drive-away price. The on-screen maths is a preview; the server ' +
            'recomputes on save and is the authoritative figure. GST is shown as the “of which” component of the ' +
            'GST-inclusive drive-away price (Australian 1/11 rule).' },
        ],
      },
      {
        id: 'dd-sale',
        heading: 'From quote to sale',
        blocks: [
          { type: 'steps', items: [
            '**Save**, then **⭳ PDF** to generate a customer-ready quote (NCCP finance disclosures print automatically).',
            '**Send to customer** — this reserves the vehicle.',
            'Mark it **Accepted** or **Declined** as the customer responds.',
            '**Complete sale…** — enter the deposit taken and delivery date; this creates the sale, marks the vehicle **SOLD**, and advances the deal.',
          ]},
          { type: 'warn', title: 'Sent/accepted quotes lock', text:
            'Once a quote leaves Draft/Sent (Accepted, Declined, etc.) its line items become read-only. Cancel and ' +
            'rebuild if the numbers need to change.' },
        ],
      },
    ],
  },

  {
    id: 'customers-diary',
    title: 'Customers, appointments & tasks',
    blurb: 'The CRM file, the dealership diary and your daily workplan — and why bookable slots appear (or don’t).',
    minutes: '7 min read',
    audience: 'Consultants · BDC · Managers',
    category: 'Daily use',
    sections: [
      {
        id: 'cd-customers',
        heading: 'Customers & Customer 360',
        blocks: [
          { type: 'p', text:
            'Open **Sales → Customers** for the directory — lifetime value, churn risk, tags and next action, filterable ' +
            'by risk and searchable by name/email/phone/tag. Open a customer for **Customer 360**: a timeline of every ' +
            'WhatsApp/SMS/voice interaction across all their leads, notes, linked leads and deals, lost-lead history, ' +
            'lifetime stats, and per-channel **consent** flags (SMS, email, WhatsApp, DNC).' },
          { type: 'note', text:
            'The 360 timeline spans every lead the customer has ever had, so it’s the single best place to see the whole ' +
            'relationship before you call.' },
        ],
      },
      {
        id: 'cd-book',
        heading: 'Book an appointment',
        blocks: [
          { type: 'p', text:
            'Book from a lead (**📅 Book**) or from **Sales → Appointments** (**+ New appointment**). Choose a type ' +
            '(Test drive, Showroom visit, Trade-in appraisal, Finance consultation, Delivery, Service, Follow-up call), ' +
            'a date, and a bookable slot; pickers for consultant, lead and customer are searchable, and picking a lead ' +
            'auto-pins its customer.' },
          { type: 'p', text: 'Appointments follow a lifecycle you drive from the diary drawer or the lead:' },
          { type: 'list', items: [
            'Requested → **Confirm** / **Cancel** (AI-booked: a single **OK**, as the customer already has the time)',
            'Confirmed → **Check in** / **Reschedule** / **No-show** / **Cancel**',
            'Checked-in → **Complete** (with an outcome: Sold / Quoted / Follow-up / No sale)',
          ]},
          { type: 'note', title: 'Double-booking', text:
            'The diary conflict-checks the consultant. If a slot clashes you’ll get a **“Book anyway”** override rather ' +
            'than a dead end.' },
        ],
      },
      {
        id: 'cd-hours',
        heading: 'Why slots appear — Working hours',
        blocks: [
          { type: 'p', text:
            'Bookable slots come from each consultant’s **weekly working-hours template**. Set them from **Platform → Team ' +
            '→ 🕑 Set working hours** (the diary settings page): enable days, set start/end times and a slot length, and ' +
            'mark the schedule Active.' },
          { type: 'warn', title: 'No bookable slots?', text:
            'A consultant with no active schedule (or one with no working days) generates no slots and won’t appear in the ' +
            '“any consultant” list. This is the usual cause of an empty slot picker.' },
        ],
      },
      {
        id: 'cd-tasks',
        heading: 'Tasks — your workplan',
        blocks: [
          { type: 'p', text:
            'Open **Sales → Tasks** for a to-do list of calls, emails, follow-ups and admin, linked to leads and ' +
            'customers. The stat cards show Due today, Overdue, Open and Done this week.' },
          { type: 'list', items: [
            '**+ New task** — title, type, priority, assignee, due date, and an optional linked lead/customer.',
            'Tick the checkbox to **complete**; **+1d** snoozes a day; filter by status, assignee and due window.',
          ]},
        ],
      },
    ],
  },

  {
    id: 'showroom',
    title: 'Showroom: inventory, test drives, loans & appraisals',
    blurb: 'Stock and the physical-vehicle operations — photos, drive agreements, courtesy cars and trade-in valuations.',
    minutes: '9 min read',
    audience: 'Sales · Stock controllers · Service',
    category: 'Daily use',
    sections: [
      {
        id: 'sh-inventory',
        heading: 'Inventory — the stock register',
        blocks: [
          { type: 'p', text:
            'Open **Showroom → Inventory**. Vehicles arrive from the dealer-system stock feed; you add or complete the ' +
            'ones it hasn’t pushed. KPI cards show Total, In stock, Reserved and Aged (60d+).' },
          { type: 'list', items: [
            '**+ Add vehicle** / **Edit** — full specs: identity (make, model, trim, VIN, stock #), specification (body, condition, transmission, fuel, drivetrain, odometer, colours, engine, power, doors, seats), registration & dates, and pricing (driveaway, RRP, cost).',
            '**Photos** — drag-and-drop JPG/PNG/WebP/GIF (or paste a URL). The first becomes primary; reassign with ★.',
            '**Status** — change inline between In stock, Reserved, Sold, In transit, Archived (saves instantly).',
            '**⭱ Import** — bulk CSV/XLSX with a downloadable template.',
          ]},
          { type: 'note', text:
            'Only make and model are required. Enter money in dollars (e.g. 58990). The days-in-stock badge turns amber ' +
            'at 60 days and red at 90 — that’s your aged-stock signal.' },
        ],
      },
      {
        id: 'sh-testdrives',
        heading: 'Test Drives',
        blocks: [
          { type: 'p', text:
            'Open **Showroom → Test Drives**. Book a drive (**+ New test drive**), then run it through its lifecycle from ' +
            'the row drawer.' },
          { type: 'steps', items: [
            '**Mark agreement signed** — records the signed drive agreement.',
            '**Start drive** — capture **KM out** and optionally fit an available **trade plate**.',
            '**Complete drive** — capture **KM in** and route notes.',
          ]},
          { type: 'warn', title: 'A drive won’t start until it’s safe to', text:
            'The agreement must be **SIGNED** and insurance **declared** before Start unlocks. Customer and vehicle are ' +
            'both required to create the booking. Trade plates are managed in the same screen.' },
        ],
      },
      {
        id: 'sh-loans',
        heading: 'Loan Cars',
        blocks: [
          { type: 'p', text:
            'Open **Showroom → Loan Cars** to manage the courtesy fleet and the loan-out ledger. **+ Add car** puts a ' +
            'vehicle in the fleet; **+ New loan** lends an **available** car (customer, due-back, KM out, fuel out, ' +
            'condition notes); **Return** captures KM in (required), fuel and condition.' },
          { type: 'note', text:
            'Only AVAILABLE cars can be lent. Any non-returned loan past its due-back time is auto-flagged **OVERDUE** and ' +
            'counted in the snapshot.' },
        ],
      },
      {
        id: 'sh-appraisals',
        heading: 'Appraisals — the trade-in pipeline',
        blocks: [
          { type: 'p', text:
            'Open **Showroom → Appraisals**. Each trade-in moves through a stage-gated pipeline: DRAFT → PENDING ' +
            'VALUATION → VALUED → APPROVED → (ACQUIRED or TENDERED) with REJECTED as an exit.' },
          { type: 'steps', items: [
            '**+ New appraisal** — capture the trade-in (rego, state, make, model, year, odometer, colour).',
            '**Run checks** — automatic **PPSR** and **recall** lookups; the badges update.',
            '**Submit for valuation**, then **Enter valuation** (trade and retail values) from the table row.',
            '**Approve** or **Reject…**, then **Convert to stock** (creates an Inventory vehicle) or **Start tender…** and take wholesaler bids.',
          ]},
          { type: 'note', text:
            'PPSR flags (ENCUMBERED, WRITTEN_OFF, STOLEN) and OPEN recalls show in red as risk signals but don’t block ' +
            'progression — the manager decides. Bids are sorted highest-first; awarding one advances the appraisal.' },
        ],
      },
    ],
  },

  {
    id: 'marketing-suite',
    title: 'Campaigns, surveys & lost-lead revival',
    blurb: 'Outbound marketing with compliance guarding, feedback surveys, and the AI engine that revives lapsed leads.',
    minutes: '9 min read',
    audience: 'Marketing · BDC · Managers',
    category: 'Daily use',
    sections: [
      {
        id: 'mk-campaigns',
        heading: 'Campaigns',
        blocks: [
          { type: 'p', text:
            'Open **Marketing → Campaigns**. Build a one-off blast or an event-triggered lifecycle drip across email, SMS ' +
            'and WhatsApp with the **+ New campaign** wizard: Basics (name, type, trigger event), Audience (segment), and ' +
            'Steps (cadence).' },
          { type: 'list', items: [
            '**Audience** — segment by lifecycle status, minimum lead score, days since last activity, and lead source; **Preview audience** shows a live match count.',
            '**Steps** — each has an offset (days), a channel, and optional subject/body overrides; merge fields like `{{first_name}}` fill per customer.',
            '**Lifecycle** — a new campaign saves as **DRAFT**; **Launch** it, then **Pause / Resume / Cancel**.',
          ]},
          { type: 'warn', title: 'Sends are compliance-guarded', text:
            'Watch the **BLOCKED (30d)** counter and each send’s reason. A high block rate usually means recipients are on ' +
            'the DNC register, not a technical fault. Cancelling a campaign skips its queued sends.' },
        ],
      },
      {
        id: 'mk-surveys',
        heading: 'Surveys',
        blocks: [
          { type: 'p', text:
            'Open **Marketing → Surveys** to author **CSAT**, **NPS** or **CUSTOM** surveys. Add questions (Scale, Text or ' +
            'Choice), **Publish** to start collecting, and **Close** to stop. The results drawer shows a colour-coded NPS ' +
            'score, a promoters/passives/detractors split, and per-question histograms; free-text answers appear under ' +
            '**View responses**.' },
        ],
      },
      {
        id: 'mk-deadleads',
        heading: 'Lost-lead revival',
        blocks: [
          { type: 'p', text:
            'Open **Marketing → Lost Leads**. A nightly scan (02:00 AEST) finds lapsed and orphaned leads, drops them in ' +
            'the **pool**, and can auto-generate revival campaigns (marked **AUTO**). The funnel tracks Queued → Contacted ' +
            '→ Replied → Converted, plus Opt-out, DNC-blocked and Failed.' },
          { type: 'steps', items: [
            'Review the pool and the auto-built batches — nothing sends until you **Launch**.',
            'Launched campaigns run an AI cadence across WhatsApp, SMS, an AI **voice** agent and email.',
            'On a live conversation, **Take over from AI** to reply yourself, then **Hand back to AI**.',
            '**Promote** a warm target into a hot lead, or move a record back to Leads/Customers.',
          ]},
          { type: 'note', title: 'Channel compliance cheat-sheet', text:
            'Prefer **WhatsApp** (the customer-initiated window is free); **SMS** is DNC + Spam-Act gated; the **voice** ' +
            'agent is high-effort — save it for warm signals. The cadence stops after 21 days regardless. Move to Twilio ' +
            'WhatsApp before pushing campaign volume — QR-paired numbers get banned for bulk.' },
        ],
      },
    ],
  },

  {
    id: 'insights-suite',
    title: 'Analytics, KPIs & compliance monitoring',
    blurb: 'Read your pipeline performance, watch the pilot success criteria, and prove lawful outbound contact.',
    minutes: '6 min read',
    audience: 'Managers · Dealer Principal · Compliance',
    category: 'Daily use',
    sections: [
      {
        id: 'in-analytics',
        heading: 'Analytics',
        blocks: [
          { type: 'p', text:
            'Open **Insights → Analytics** for a date-ranged performance dashboard (7d / 30d / 90d, or a custom range). ' +
            'It covers the lead funnel with step-conversion, lead sources, first-contact response times (P50/P90/P95), a ' +
            'consultant leaderboard, appointment show/no-show rates, campaign attribution, and an **AI contribution** ' +
            'section comparing AI vs human win rates.' },
        ],
      },
      {
        id: 'in-kpis',
        heading: 'Pilot KPIs',
        blocks: [
          { type: 'p', text:
            'Open **Insights → Pilot KPIs** for the go-live scorecard: each success metric shows green when it meets its ' +
            'target and amber when it doesn’t. It tracks the 90-second first-contact SLA, voice turn latency (p95/p99), ' +
            'lost-lead reply rate, test-drive conversion, service approval rate, critical-triage escalation, and the two ' +
            'compliance gates that must stay at zero — **blocked outbound** and **DNC bypassed**.' },
        ],
      },
      {
        id: 'in-compliance',
        heading: 'Compliance monitor',
        blocks: [
          { type: 'p', text:
            'Open **Insights → Compliance** for the live ACMA DNC and Spam-Act consent ledger, backed by an immutable ' +
            'audit log. Tiles show DNC checks and blocks, consent granted/revoked, and audit failures over a 7/30/90-day ' +
            'window; filter chips narrow the log to DNC, Consent, Audit or Blocked events.' },
          { type: 'note', text:
            '**Download audit pack** is the export-for-regulators action. Any non-zero “DNC blocked” is the system doing ' +
            'its job — a block means an unlawful contact was prevented.' },
        ],
      },
    ],
  },

  /* ═══════════════════════ SETUP & ADMIN ═══════════════════════ */
  {
    id: 'twilio-setup',
    title: 'Connect Twilio (SMS, calls & WhatsApp)',
    category: 'Setup & admin',
    blurb: 'Bring your own Twilio account so SpectraIQ can answer your number’s messages and calls.',
    minutes: '20–30 min',
    audience: 'Dealer Principal',
    sections: [
      {
        id: 'twilio-what',
        heading: 'What you’re actually setting up',
        blocks: [
          { type: 'p', text:
            'SpectraIQ doesn’t own any phone numbers — it uses yours, bought and billed through your own Twilio account. ' +
            'You keep control of the numbers, you see exactly what you’re spending in Twilio’s own billing, and SpectraIQ ' +
            'never has more access than the credentials you paste in. You can revoke them at any time.' },
          { type: 'table', head: ['Channel', 'What SpectraIQ does with it'], rows: [
            ['SMS', 'Receives inbound texts, replies with the AI agent, sends lost-lead campaigns'],
            ['Voice', 'Answers inbound calls — with the AI voice agent, or by forwarding to a person'],
            ['WhatsApp', 'Two-way WhatsApp conversations (there’s a real decision in this one — see below)'],
          ]},
          { type: 'p', text:
            'The three are independent. You can set up SMS today and voice next month.' },
        ],
      },
      {
        id: 'twilio-account',
        heading: 'Step 1 — Create a Twilio account',
        blocks: [
          { type: 'steps', items: [
            'Sign up at twilio.com/try-twilio and verify your email and mobile.',
            'Add a payment method under Billing → Payment methods.',
          ]},
          { type: 'warn', title: 'Upgrade before you go live', text:
            'Trial accounts can only message numbers you’ve verified with Twilio. That will make SpectraIQ look broken ' +
            'when a real customer texts in. Add a card and upgrade first.' },
          { type: 'warn', title: 'Australian dealers: start this on day one', text:
            'To buy an Australian number, Twilio needs an approved regulatory bundle (ABN + local address) under ' +
            'Phone Numbers → Regulatory Compliance. It takes one to two business days. Leaving it late is the single ' +
            'most common cause of a delayed go-live.' },
        ],
      },
      {
        id: 'twilio-number',
        heading: 'Step 2 — Buy a phone number',
        blocks: [
          { type: 'steps', items: [
            'Twilio console → Phone Numbers → Manage → Buy a number.',
            'Set the country to your market.',
            'Tick the capabilities you need — Voice, SMS and MMS for a full setup.',
            'Choose a number and buy it.',
          ]},
          { type: 'table', head: ['Type', 'Good for', 'Watch out for'], rows: [
            ['Local (+61 2 …)', 'Looks local, cheapest', 'Needs a regulatory bundle in AU'],
            ['Mobile (+61 4 …)', 'Best SMS deliverability to consumers', 'Slightly pricier'],
            ['Toll-free (1800 …)', 'Recognisable inbound line', 'SMS needs separate verification'],
          ]},
          { type: 'note', text:
            'For a dealership doing customer SMS, a mobile number is usually right — texts from a landline-looking ' +
            'number get ignored more often. You can buy several (a sales line and a service line); SpectraIQ configures ' +
            'each one independently.' },
        ],
      },
      {
        id: 'twilio-creds',
        heading: 'Step 3 — Get your credentials',
        blocks: [
          { type: 'p', text: 'On the Twilio console dashboard, under Account Info:' },
          { type: 'list', items: [
            '**Account SID** — starts with `AC`, 34 characters. Identifies your account.',
            '**Auth Token** — click *Show*. This is a password. Treat it like one.',
          ]},
          { type: 'note', title: 'Recommended: create an API Key too', text:
            'The Auth Token is also the key Twilio uses to sign the webhooks it sends us — so rotating it breaks every ' +
            'live webhook until SpectraIQ is updated. An API Key can be revoked on its own, without that side effect. ' +
            'Create one under Account → API keys & tokens → Create API key (type: Standard). The secret is shown exactly once.' },
          { type: 'p', text:
            'Paste the Account SID, Auth Token *and* the API key pair into SpectraIQ. It uses the API key for day-to-day ' +
            'calls, and the Auth Token only to verify that inbound webhooks really came from Twilio.' },
        ],
      },
      {
        id: 'twilio-connect',
        heading: 'Step 4 — Connect Twilio in SpectraIQ',
        blocks: [
          { type: 'steps', items: [
            'Go to Settings → Channels.',
            'Paste your Account SID and Auth Token under Connect Twilio.',
            'Click Show advanced and paste the API Key SID and Secret if you made them.',
            'Set the Public webhook base URL — the public HTTPS address Twilio should call SpectraIQ back on, e.g. https://api.yourdealership.com. If you’re unsure, ask whoever runs your SpectraIQ instance.',
            'Click Connect Twilio.',
          ]},
          { type: 'p', text:
            'SpectraIQ immediately calls Twilio to check the credentials. You’ll see either the account name and a green ' +
            'Connected, or the exact error Twilio returned.' },
          { type: 'note', title: 'Your credentials are encrypted', text:
            'They’re encrypted (AES-256-GCM) before storage and never shown again — the screen only ever displays a masked ' +
            'SID like AC••••••••4e3. If you lose your token, rotate it in Twilio. There’s no way to read it back out of SpectraIQ.' },
        ],
      },
      {
        id: 'twilio-numbers',
        heading: 'Step 5 — Sync your numbers and switch on the channels',
        blocks: [
          { type: 'steps', items: [
            'Click Sync from Twilio. SpectraIQ pulls in every number on the account and what each one can do.',
            'For each number, tick SMS if you want SpectraIQ to receive texts and reply.',
            'Choose how calls are handled: Don’t answer / AI voice agent / Forward to a person.',
            'If you have more than one number, pick which is the default SMS sender and caller ID.',
            'Click Provision on each number.',
          ]},
          { type: 'warn', title: 'Provision is the step everyone forgets', text:
            'It writes SpectraIQ’s webhook addresses into Twilio, which is what actually makes messages and calls arrive. ' +
            'Without it, everything is configured on both sides and nothing works. The number shows “Webhooks set” when it’s done.' },
          { type: 'p', text:
            'If you’d rather do it by hand, expand Webhook URLs on the Twilio card and paste them into Twilio yourself ' +
            '(Phone Numbers → Manage → Active numbers → your number). Set the method to HTTP POST on each:' },
          { type: 'table', head: ['Twilio field', 'Paste this'], rows: [
            ['Messaging → A message comes in', '<your-host>/api/v1/webhooks/twilio/sms'],
            ['Voice → A call comes in', '<your-host>/api/v1/webhooks/twilio/voice'],
            ['Voice → Call status changes', '<your-host>/api/v1/webhooks/twilio/voice/status'],
          ]},
        ],
      },
      {
        id: 'twilio-whatsapp',
        heading: 'Step 6 — WhatsApp (there’s a real decision here)',
        blocks: [
          { type: 'warn', title: 'You can only run WhatsApp one way at a time', text:
            'QR pairing OR Twilio — never both. This isn’t an arbitrary limit. WhatsApp is two-way: every message a ' +
            'customer sends gets an AI reply, and that reply has to come from the same WhatsApp number they messaged. ' +
            'If both were live, someone who messaged your showroom mobile would be answered from a Twilio number they’ve ' +
            'never seen — a separate thread, from a stranger.' },
          { type: 'table', head: ['', 'Scan a QR code', 'Twilio WhatsApp Business'], rows: [
            ['Cost', 'Free', 'Per message'],
            ['Time to live', '~1 minute', 'Hours to days (Meta review)'],
            ['Number', 'The one you already use', 'A new business number'],
            ['Ban risk', 'Real — unofficial client', 'None'],
            ['Campaigns / bulk', 'No', 'Yes, with templates'],
            ['Best for', 'Piloting, conversational volume', 'Production, campaigns, volume'],
          ]},
          { type: 'note', title: 'Our recommendation', text:
            'Pilot on QR to prove the value quickly, then move to Twilio before you turn on lost-lead campaigns. ' +
            'Pushing campaign volume through a QR-paired number is the fastest way to get it banned.' },
          { type: 'p', text: '**To set up QR:** Settings → Channels → WhatsApp → Scan a QR code, then scan it from ' +
            'WhatsApp → Settings → Linked devices → Link a device.' },
          { type: 'p', text: '**To set up Twilio WhatsApp:**' },
          { type: 'steps', items: [
            'In Twilio: Messaging → Senders → WhatsApp senders → New WhatsApp Sender.',
            'Pick the Twilio number you want to use.',
            'Complete the Meta business profile: name, category, description, logo.',
            'Submit, and wait for Meta’s review email.',
            'Once approved: Settings → Channels → WhatsApp → Twilio WhatsApp Business → choose the number → Use this sender.',
          ]},
          { type: 'note', title: 'Just testing?', text:
            'Twilio’s WhatsApp Sandbox (Messaging → Try it out) works immediately with no registration — but everyone who ' +
            'messages it must first send the join code to Twilio’s sandbox number. Testing only. Never point real customers at it.' },
          { type: 'p', text:
            '**Switching later** is a button in Settings. SpectraIQ warns you first, because switching disconnects the old ' +
            'provider: a QR pairing is wiped and must be re-scanned, and inbound stops on the old number immediately. ' +
            'Your conversation history is untouched. Do it outside opening hours.' },
        ],
      },
      {
        id: 'twilio-test',
        heading: 'Step 7 — Test it end to end',
        blocks: [
          { type: 'p', text: 'Use a real handset, not a simulator.' },
          { type: 'table', head: ['Test', 'Expect'], rows: [
            ['Text your Twilio number', 'Lead appears within seconds; the AI replies'],
            ['Ring it (AI voice agent mode)', 'The AI answers and holds a conversation'],
            ['Ring it (Forward mode)', 'Your nominated phone rings'],
            ['WhatsApp the connected number', 'Message appears; AI replies from the same number'],
            ['Reply STOP', 'Immediately opted out — no further messages'],
            ['Check Settings → Channels', 'The call is listed under Recent calls with its duration'],
          ]},
        ],
      },
      {
        id: 'twilio-trouble',
        heading: 'Troubleshooting',
        blocks: [
          { type: 'p', text: '**“Nothing arrives when I text the number.”** Almost always the webhook. In order:' },
          { type: 'steps', items: [
            'Is the number showing “Webhooks set”? If it says “No webhooks”, click Provision.',
            'Is your webhook base URL publicly reachable over HTTPS? Twilio has to reach it from the internet — localhost and Docker hostnames won’t work. For local testing use a tunnel like ngrok.',
            'Check Twilio’s own debugger: Monitor → Logs → Errors. It records every failed webhook delivery with the HTTP status it got back.',
          ]},
          { type: 'p', text: '**“Twilio rejected these credentials.”** Re-copy the Account SID (must start with `AC`) and the Auth Token. If you used an API key, check you pasted the Key SID (`SK…`) and not the Account SID, and that the key belongs to the same account.' },
          { type: 'p', text: '**“Forbidden” / messages silently dropped.** SpectraIQ validates Twilio’s signature on every inbound webhook — it’s the only thing authenticating those endpoints, so it can’t be skipped. It fails when the URL Twilio dialled doesn’t match the URL SpectraIQ thinks it received, which is nearly always a reverse proxy not forwarding X-Forwarded-Proto / X-Forwarded-Host.' },
          { type: 'p', text: '**“Calls ring out.”** Check the voice mode isn’t “Don’t answer”. If forwarding, the destination must be E.164 (+61400111222, not 0400 111 222).' },
          { type: 'p', text: '**“WhatsApp arrives but replies don’t.”** Check which provider is active. If messages are still arriving on the number you switched *away* from, that transport wasn’t fully torn down — remove the webhook from the old Twilio sender, or remove the linked device from the phone.' },
        ],
      },
      {
        id: 'twilio-cost',
        heading: 'What it costs',
        blocks: [
          { type: 'p', text: 'Twilio bills you directly. Roughly, in Australia:' },
          { type: 'table', head: ['Item', 'Approx cost'], rows: [
            ['Local/mobile number', '$3–6 / month'],
            ['Outbound SMS', '~$0.05 each'],
            ['Inbound SMS', '~$0.01 each'],
            ['Voice (inbound)', '~$0.01 / minute'],
            ['WhatsApp conversation', '~$0.02–0.08 per 24h conversation'],
          ]},
          { type: 'note', text:
            'These move — check twilio.com/pricing for current rates. A dealership doing a few hundred conversations a ' +
            'month typically lands in the tens of dollars.' },
        ],
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════ */
  {
    id: 'dealer-onboarding',
    title: 'Onboard a new dealership',
    blurb: 'From “we signed a dealer” to “the AI is answering their customers”.',
    minutes: '~90 min hands-on',
    audience: 'Dealer Principal / PMDG Ops',
    category: 'Setup & admin',
    sections: [
      {
        id: 'onb-shape',
        heading: 'The shape of it',
        blocks: [
          { type: 'code', text:
`Day 1   Provision tenant ──┬─► Start Twilio regulatory bundle  (1–2 days)
                           └─► Start WhatsApp sender registration (if using Twilio WA)
Day 1   Import dealer-system data, create users, set business hours
Day 2   Connect Twilio, sync numbers, provision webhooks
Day 2   Configure the AI: persona, voice, triage rules
Day 3   Test end to end with real handsets
Day 3   Go live — one channel at a time` },
          { type: 'warn', title: 'Start the slow things on day one', text:
            'Most of the elapsed time is waiting on other people — Twilio’s regulatory approval and Meta’s WhatsApp review. ' +
            'The single biggest cause of a slow onboarding is leaving the regulatory bundle until you need the number.' },
        ],
      },
      {
        id: 'onb-provision',
        heading: 'Stage 1 — Provision the dealership',
        blocks: [
          { type: 'steps', items: [
            'Create the dealership record. This allocates the tenant and creates its isolated database schema — every dealership’s data lives in its own schema, not a shared table with a filter on it.',
            'Create the identity organisation so their staff can log in.',
            'Create the first user: the dealer principal. They can create everyone else.',
            'Confirm they can log in.',
          ]},
          { type: 'note', text: 'Checkpoint: the dealer principal signs in and sees an empty dashboard. Nothing is connected yet — that’s expected.' },
        ],
      },
      {
        id: 'onb-slow',
        heading: 'Stage 2 — Kick off the slow things',
        blocks: [
          { type: 'p', text: 'Do these first, because they have other people in the loop.' },
          { type: 'list', items: [
            '**Twilio account + regulatory bundle.** Sign up, add a payment method, and (for an Australian number) submit the regulatory bundle — ABN and local address. One to two business days.',
            '**WhatsApp sender registration** — only if they’re going to use Twilio for WhatsApp. Submit it now; Meta’s review is the long pole. If they’re using QR pairing instead, skip this entirely.',
          ]},
        ],
      },
      {
        id: 'onb-world',
        heading: 'Stage 3 — Load the dealership’s world',
        blocks: [
          { type: 'p', text: '**Users and roles.** Give people the least access that lets them do their job:' },
          { type: 'table', head: ['Role', 'Sees', 'Use for'], rows: [
            ['Dealer Principal', 'Everything, incl. channel settings and billing', 'The owner. One or two people'],
            ['Sales Manager', 'All leads, pipeline, team performance', 'Sales managers'],
            ['BDC Agent', 'The lead queue and conversations', 'The BDC team'],
            ['Sales Consultant', 'Only their assigned leads', 'Consultants on the floor'],
            ['Read-only Auditor', 'Compliance and audit logs, nothing else', 'External compliance'],
          ]},
          { type: 'note', text:
            'Only Dealer Principal can touch Twilio credentials or reroute phone numbers. That’s deliberate — those ' +
            'settings can redirect the dealership’s phone traffic and spend its money.' },
          { type: 'p', text:
            '**Dealer system integration.** Connect the dealership’s management system. This is what gives the AI something ' +
            'to talk about — real stock, real service history. Without it the AI can hold a conversation but can’t answer ' +
            '“do you have a blue RAV4 in stock”, which is most of the value.' },
          { type: 'p', text:
            '**Business hours.** Not cosmetic — this is the ACMA-compliant calling window. Outbound voice and SMS are ' +
            'hard-blocked outside it, at the gateway, regardless of what the AI wants to do.' },
          { type: 'p', text:
            '**Compliance.** Confirm the DNC check is on and consent rules are right. These are enforced as gates, not as ' +
            'prompt instructions — the AI cannot talk its way past them.' },
        ],
      },
      {
        id: 'onb-channels',
        heading: 'Stage 4 — Connect the phone channels',
        blocks: [
          { type: 'p', text: 'Follow the Twilio guide — paste credentials, sync numbers, set per-number routing, provision.' },
          { type: 'warn', title: 'Provision is the step that breaks everything when skipped', text:
            'Configured on both sides but not provisioned = silence. Checkpoint: text the number from a real handset. ' +
            'It should appear under Leads within seconds and the AI should reply.' },
        ],
      },
      {
        id: 'onb-whatsapp',
        heading: 'Stage 5 — WhatsApp: one way or the other',
        blocks: [
          { type: 'p', text:
            'A dealership runs WhatsApp through exactly one provider. Every inbound message gets an AI reply, and the reply ' +
            'must come from the same number the customer messaged — so SpectraIQ makes you choose.' },
          { type: 'note', title: 'Recommended path', text:
            'Pilot on QR to prove value quickly, then move to Twilio before turning on lost-lead campaigns. Sending campaign ' +
            'volume through a QR-paired number is the fastest way to get it banned.' },
        ],
      },
      {
        id: 'onb-ai',
        heading: 'Stage 6 — Configure the AI',
        blocks: [
          { type: 'list', items: [
            '**Voice persona.** Pick the voice, accent and greeting. Say the greeting out loud before you commit — it’s the first thing every caller hears.',
            '**Triage rules.** These decide when a conversation gets flagged for a human. The defaults are sensible; make sure escalation on “manager”, “complaint”, “lawyer” is on.',
            '**Lost-lead campaigns — leave OFF for week one.** Get inbound working and trusted first.',
          ]},
        ],
      },
      {
        id: 'onb-test',
        heading: 'Stage 7 — Test with real handsets',
        blocks: [
          { type: 'table', head: ['Test', 'Expect'], rows: [
            ['Text the number', 'Lead appears in seconds; AI replies sensibly'],
            ['Ring it (AI mode)', 'AI answers, converses, offers to book'],
            ['Ring it (forward mode)', 'Your nominated phone rings'],
            ['WhatsApp it', 'Message appears; AI replies from the same number'],
            ['Ask something it shouldn’t answer', 'AI declines rather than inventing an answer'],
            ['Say “I want a manager”', 'Escalates to a human'],
            ['Reply STOP', 'Immediately opted out'],
          ]},
          { type: 'note', text:
            'The last two matter more than they look. STOP handling and the audit trail are what a regulator asks about.' },
        ],
      },
      {
        id: 'onb-golive',
        heading: 'Stage 8 — Go live, in this order',
        blocks: [
          { type: 'steps', items: [
            'Inbound SMS + WhatsApp. Lowest risk — you’re responding to people who chose to contact you.',
            'Inbound voice. Start with forward-to-a-person if the dealer is nervous, then move to the AI agent once they’ve heard it work.',
            'Service reminders. Outbound, but expected and welcome.',
            'Lost-lead campaigns. Highest volume, coldest audience. Last.',
          ]},
          { type: 'p', text:
            'For the first week, have someone actually read the conversations. Triage surfaces the ones worth reading — ' +
            'sentiment drops, quality flags, escalations.' },
        ],
      },
      {
        id: 'onb-failures',
        heading: 'Common onboarding failures',
        blocks: [
          { type: 'table', head: ['Symptom', 'Cause', 'Fix'], rows: [
            ['Everything configured, nothing arrives', 'Webhooks not provisioned', 'Settings → Channels → Provision'],
            ['Nothing arrives, webhooks ARE provisioned', 'Webhook base URL not publicly reachable', 'Must be public HTTPS — not localhost, not a Docker hostname'],
            ['Twilio rejected the credentials', 'Wrong SID, or API key from another account', 'Re-copy from the Twilio dashboard'],
            ['AI answers but knows nothing about stock', 'Dealer system not connected', 'Settings → Integrations · Dealer system'],
            ['WhatsApp number banned', 'QR pairing used for campaign volume', 'Switch to Twilio WhatsApp Business'],
            ['Outbound blocked at odd hours', 'Business hours = the ACMA calling window', 'Settings → Business hours'],
          ]},
        ],
      },
      {
        id: 'onb-checklist',
        heading: 'The checklist',
        blocks: [
          { type: 'code', text:
`[ ] Dealership tenant provisioned; schema created
[ ] Dealer principal can log in
[ ] Twilio account created, payment method added
[ ] Regulatory bundle submitted          <-- day one
[ ] WhatsApp sender submitted (if using Twilio WA)   <-- day one
[ ] Users created with least-privilege roles
[ ] Dealer system connected; stock and customers visible
[ ] Business hours set (= ACMA calling window)
[ ] Compliance: DNC on, consent rules confirmed
[ ] Twilio credentials connected and verified
[ ] Numbers synced
[ ] Per-number routing set (SMS / voice mode / forwarding)
[ ] Webhooks PROVISIONED on every number      <-- the one people forget
[ ] WhatsApp provider chosen (QR or Twilio — one, not both)
[ ] Voice persona and greeting reviewed out loud
[ ] Triage rules confirmed; escalation keywords on
[ ] Tested from a real handset: SMS, call, WhatsApp
[ ] Tested: STOP opt-out works
[ ] Tested: escalation to human works
[ ] Lost-lead campaigns OFF for week one
[ ] Someone assigned to read Triage daily for week one` },
        ],
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════ */
  {
    id: 'whatsapp-choice',
    title: 'WhatsApp: QR or Twilio?',
    blurb: 'Why you have to choose one — and which one is right for you.',
    minutes: '3 min read',
    audience: 'Dealer Principal',
    category: 'Setup & admin',
    sections: [
      {
        id: 'wa-why',
        heading: 'Why you can’t have both',
        blocks: [
          { type: 'p', text:
            'SpectraIQ makes you pick one WhatsApp provider. This surprises people, so it’s worth explaining rather than ' +
            'just enforcing.' },
          { type: 'p', text:
            'WhatsApp isn’t a read-only feed — it’s a conversation. Every message a customer sends gets an AI reply, and ' +
            '**that reply has to come from the same WhatsApp number they messaged.** That’s just how a conversation works.' },
          { type: 'p', text:
            'If both providers were connected, a customer who messaged your showroom mobile would be answered from a Twilio ' +
            'business number they’ve never seen. New thread, unknown sender, no context. And on Twilio’s side it would count ' +
            'as an unsolicited business-initiated message outside the 24-hour customer-service window — so Twilio would ' +
            'reject it as un-templated, and the customer would get nothing at all.' },
          { type: 'note', text:
            'So the choice isn’t about receiving. It’s about being able to answer.' },
        ],
      },
      {
        id: 'wa-compare',
        heading: 'The comparison',
        blocks: [
          { type: 'table', head: ['', 'Scan a QR code', 'Twilio WhatsApp Business'], rows: [
            ['Cost', 'Free', 'Per message, billed by Twilio'],
            ['Time to live', '~1 minute', 'Hours to days (Meta reviews it)'],
            ['Number', 'The one your customers already know', 'A new business number'],
            ['Ban risk', 'Real — it’s an unofficial client', 'None — it’s the sanctioned API'],
            ['Campaigns / bulk', 'No', 'Yes, with message templates'],
            ['Delivery reporting', 'Basic', 'Full'],
            ['Best for', 'Piloting; conversational volume', 'Production; campaigns; volume'],
          ]},
        ],
      },
      {
        id: 'wa-recommend',
        heading: 'What we’d do',
        blocks: [
          { type: 'p', text:
            '**Start on QR.** It’s free and live in a minute, so you can prove the AI is worth having before spending ' +
            'anything or waiting on Meta.' },
          { type: 'p', text:
            '**Move to Twilio before you turn on lost-lead campaigns.** Campaign volume through a QR-paired number is the ' +
            'single fastest way to get that number banned by WhatsApp — and it’ll be the number your customers know.' },
          { type: 'warn', title: 'Switching disconnects the old one', text:
            'Switching is a button in Settings → Channels, but it’s destructive: the QR pairing is wiped and has to be ' +
            're-scanned, and inbound stops on the old number immediately. Your CRM history is never deleted. Do it outside ' +
            'opening hours.' },
        ],
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════ */
  /* In-product mirror of docs/SpectraIQ_Voice_Providers_Setup_Guide.md —
     if you change one, change the other. */
  {
    id: 'voice-providers',
    title: 'Configure voice providers (STT & TTS)',
    blurb: 'Choose which speech-to-text and text-to-speech service the AI voice agent uses, and connect the credentials.',
    minutes: '10–15 min',
    audience: 'Dealer Principal',
    category: 'Setup & admin',
    sections: [
      {
        id: 'vp-what',
        heading: 'What you’re choosing',
        blocks: [
          { type: 'p', text:
            'When the AI agent answers a call, two services do the ears and the mouth: a **speech-to-text (STT)** provider ' +
            'turns the caller’s voice into words, and a **text-to-speech (TTS)** provider turns the AI’s reply back into ' +
            'voice. Both are configurable per dealership under Settings → Voice agents — you bring your own account ' +
            'with the provider, paste the key, and SpectraIQ does the rest.' },
          { type: 'table', head: ['Side', 'Providers', 'Default'], rows: [
            ['Speech-to-text', 'Deepgram · Azure AI Speech · Google Speech v2 · OpenAI Realtime', 'Deepgram Nova-2'],
            ['Text-to-speech', 'ElevenLabs · AWS Polly Neural · Azure Neural TTS · Google Cloud TTS · OpenAI TTS', 'ElevenLabs'],
          ]},
          { type: 'note', text:
            'You can mix vendors freely — e.g. Deepgram for listening and Azure for speaking. Changes apply to new calls ' +
            'within about a minute of saving; calls already in progress finish on the old settings.' },
          { type: 'p', text:
            'If you never touch this screen, calls run on the platform defaults configured by your SpectraIQ operator. ' +
            'Configure it when you want your own billing relationship with the speech vendor, a specific voice, or a ' +
            'different language/accent model.' },
        ],
      },
      {
        id: 'vp-pick',
        heading: 'Which should you pick?',
        blocks: [
          { type: 'p', text:
            'Every combination works. The differences are latency, voice quality and price — the AI answers a caller ' +
            'within a ~1.2 second budget, and the TTS choice is the biggest lever on how snappy it feels.' },
          { type: 'table', head: ['Provider', 'Strength', 'Worth knowing'], rows: [
            ['Deepgram (STT)', 'Fastest streaming transcription; the tuned default', 'Nova-2 model, en-AU supported'],
            ['Azure Speech (STT)', 'Enterprise SLA, AU region (australiaeast)', 'The same key also serves Azure TTS'],
            ['Google Speech v2 (STT)', 'Strong multilingual coverage', 'API key + Project ID, or a service-account JSON'],
            ['OpenAI Realtime (STT)', 'Very accurate on messy audio', 'Language is two-letter, e.g. “en”'],
            ['ElevenLabs (TTS)', 'Most natural voices, lowest first-word latency', 'Voice cloning available'],
            ['AWS Polly (TTS)', 'Cheap, reliable, AU voice “Olivia”', 'The designated fallback voice'],
            ['Azure Neural (TTS)', 'Natural AU voices, e.g. en-AU-NatashaNeural', 'Emits telephony audio natively — very efficient'],
            ['Google Cloud (TTS)', 'Neural2 / WaveNet voices', 'Simplest auth: a plain API key'],
            ['OpenAI (TTS)', 'Good voices; one vendor for STT + TTS + LLM', 'Slightly higher latency (audio resampling)'],
          ]},
        ],
      },
      {
        id: 'vp-keys',
        heading: 'Step 1 — Get your credentials',
        blocks: [
          { type: 'p', text: 'Only collect the ones for the providers you picked:' },
          { type: 'steps', items: [
            '**Deepgram** — console.deepgram.com → API Keys → Create key.',
            '**Azure AI Speech** — Azure portal → create a “Speech service” resource in your region (australiaeast for AU) → Keys and Endpoint → Key 1. One key serves both STT and TTS.',
            '**Google STT** — simplest: APIs & Services → Credentials → Create credentials → API key (with the Speech-to-Text API enabled), plus your Project ID from the console home page — SpectraIQ needs both together. Alternative: IAM & Admin → Service Accounts → create one with the “Cloud Speech Client” role → Keys → Add key → JSON, and paste the whole JSON file instead.',
            '**Google TTS** — APIs & Services → Credentials → Create credentials → API key, with the Cloud Text-to-Speech API enabled. (The service-account JSON works here too.)',
            '**OpenAI** — platform.openai.com → API keys → Create new secret key.',
            '**ElevenLabs** — elevenlabs.io → Profile → API key. Also copy the Voice ID of the voice you want from Voices.',
            '**AWS Polly** — nothing to create if SpectraIQ runs on AWS with an IAM role (recommended: leave the key fields blank). Otherwise create an IAM user allowed polly:SynthesizeSpeech and note its access key pair.',
          ]},
          { type: 'warn', title: 'Billing is yours', text:
            'These are live billable credentials against your own vendor account. Usage charges (per minute of audio / ' +
            'per character of speech) appear on that vendor’s bill, not SpectraIQ’s.' },
        ],
      },
      {
        id: 'vp-connect',
        heading: 'Step 2 — Connect in Settings',
        blocks: [
          { type: 'steps', items: [
            'Open Settings → Voice agents.',
            'Pick your speech-to-text provider and fill in its fields (key, region or model as prompted).',
            'Pick your text-to-speech provider, fill in the key and the voice — this is the voice your customers will hear.',
            'Save changes. The status pill shows “Not verified”.',
            'Click “Test connection”. SpectraIQ checks both credentials against the vendors and the pill flips to “Verified” — or tells you exactly which side failed and why.',
          ]},
          { type: 'note', text:
            'Keys are encrypted before they’re stored (AES-256-GCM) and never shown again — the screen only displays a ' +
            'masked form like sk••••••••x9f2. To replace a key, type a new one over the placeholder; to keep it, leave ' +
            'the field blank. To delete a stored secret — say, remove a Google API key so you can switch to a service-account JSON — use the “Clear” button under that field, then Save. Changing provider clears all stored secrets for that side automatically.' },
          { type: 'warn', title: 'If the test fails', text:
            'Azure: check the region matches where you created the resource (australiaeast vs eastus is the usual culprit). ' +
            'Google: make sure you pasted the entire JSON file including the braces. Deepgram/OpenAI/ElevenLabs: the key ' +
            'was probably truncated on copy — re-copy it.' },
        ],
      },
      {
        id: 'vp-verify',
        heading: 'Step 3 — Hear it',
        blocks: [
          { type: 'steps', items: [
            'Call one of your AI-answered numbers (Settings → Channels shows which numbers are set to “AI voice agent”).',
            'Say something and listen: the reply should come back in your chosen voice within about a second.',
            'If the agent answers in the old voice, wait a minute — the voice loop refreshes tenant settings on a 60-second cache — and call again.',
          ]},
          { type: 'note', text:
            'The voice agent needs Twilio voice working first. If calls don’t reach the AI at all, that’s the Twilio ' +
            'side — see “Connect Twilio (SMS, calls & WhatsApp)”, the per-number voice mode step.' },
        ],
      },
      {
        id: 'vp-mictest',
        heading: 'Test with your microphone — no phone call needed',
        blocks: [
          { type: 'p', text:
            'The **Mic test** card on the Voice agents screen lets you talk to the AI agent straight from your ' +
            'browser. It runs the exact same pipeline as a phone call — your chosen speech-to-text, the AI agent, ' +
            'and your chosen voice — so what you hear is what a customer would hear.' },
          { type: 'steps', items: [
            'Save and verify your providers first (the button stays disabled until then).',
            'Click “Start mic test” and allow microphone access when the browser asks.',
            'Talk. Your words appear in the transcript as STT hears them; the AI replies out loud in your configured voice.',
            'Watch the latency chips — a turn under 1.2 s is on budget; interrupting the AI mid-sentence should cut it off (barge-in).',
          ]},
          { type: 'note', text:
            'Use headphones. Echo cancellation is on, but open speakers can leak the AI’s own voice back into the ' +
            'mic and trigger barge-in. Sessions run on a 5-minute ticket — just click Start again if it expires.' },
          { type: 'warn', title: 'If it won’t connect', text:
            'The mic test talks to the voice loop directly, so your browser must be able to reach it (locally: port ' +
            '8002; in production: the same public URL Twilio streams to). Microphone capture also requires HTTPS or ' +
            'localhost. A “token invalid” error usually means the shared secret isn’t configured on the voice loop.' },
        ],
      },
      {
        id: 'vp-security',
        heading: 'Security & fallback behaviour',
        blocks: [
          { type: 'list', items: [
            'Keys live encrypted (AES-256-GCM) in your dealership’s own schema; only SUPER_ADMIN and DEALER_PRINCIPAL can change them, and the API never returns a stored secret.',
            'Decrypted credentials travel only over the internal service network to the voice loop — never to the browser.',
            'If your configured provider is unreachable when a call starts, the call is rejected cleanly rather than answered mute; a mid-call synthesis failure skips that sentence instead of dropping the call.',
            'Removing or disabling your settings makes calls fall back to the platform defaults automatically.',
          ]},
        ],
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════ */
  {
    id: 'admin-config',
    title: 'Team, roles, AI behaviour & routing',
    blurb: 'The dealership-level controls: who can do what, how the AI talks, how leads are routed, and how calls are QA’d.',
    minutes: '10 min read',
    audience: 'Dealer Principal / Sales Manager',
    category: 'Setup & admin',
    sections: [
      {
        id: 'ac-team',
        heading: 'Team & roles',
        blocks: [
          { type: 'p', text:
            'Open **Platform → Team** to invite people and set roles. Inviting or editing needs **Dealer Principal** or ' +
            '**Super Admin**; hard-deleting a user is Super-Admin-only. Invited users are created in your identity ' +
            'provider and flip from *Invited* to *Active* automatically on first login.' },
          { type: 'table', head: ['Role', 'Scope'], rows: [
            ['Dealer Principal', 'Full control of this dealership, incl. channels and billing'],
            ['Sales Manager', 'Oversees consultants and the lead board'],
            ['Service Manager', 'Oversees service advisors'],
            ['BDC Agent', 'Handles AI handoffs and the lead queue'],
            ['Sales Consultant', 'Works their assigned leads; bookable in the diary'],
            ['Service Advisor', 'Workshop bookings and the on-the-day journey'],
            ['Auditor', 'Read-only access to compliance and the audit log'],
          ]},
          { type: 'note', text:
            'Give people the least access that lets them do their job. **Super Admin** is a PMDG cross-tenant grant and ' +
            'can’t be assigned from the dealership UI.' },
        ],
      },
      {
        id: 'ac-ai',
        heading: 'AI Behaviour',
        blocks: [
          { type: 'p', text:
            'Open **Platform → Settings → AI Behaviour** to tune how the assistant talks: **tone** ' +
            '(Friendly / Professional / Concise / Enthusiastic), **creativity** (temperature), **language**, **scope mode** ' +
            '(Strict keeps it to automotive topics), and whether it may **hand off to a human**.' },
          { type: 'warn', title: 'Custom prompt, guardrails stay', text:
            'Turn on **Use a custom system prompt** to fully replace the default wording. The compliance safety rules ' +
            '(DNC, consent, mandatory AI disclaimer, PII redaction) are always appended automatically — a custom prompt ' +
            'cannot remove them. Changes apply to conversations started after you save.' },
          { type: 'note', text:
            'On calls, the assistant speaks with the tone and prompt set here; the actual **voice** (TTS) and speech ' +
            'recognition (STT) are chosen under **Settings → Voice agents**.' },
        ],
      },
      {
        id: 'ac-routing',
        heading: 'Lead auto-assignment',
        blocks: [
          { type: 'p', text:
            'Open **Platform → Pipeline Settings → Assignment rules**. Rules are evaluated in priority order for every ' +
            'new lead; each targets a pool of consultants (leave it empty to use all active consultants).' },
          { type: 'table', head: ['Strategy', 'What it does'], rows: [
            ['Round-robin', 'Rotate new leads evenly across the chosen consultants'],
            ['Load-balanced', 'Give each lead to whoever has the fewest open leads (optional cap)'],
            ['VIP', 'When a lead’s score is at/above a threshold, rotate it to the chosen consultants'],
            ['Territory', 'Route leads whose postcode starts with a listed prefix'],
            ['Source', 'Route leads from selected lead sources (Carsales, Facebook, etc.)'],
          ]},
          { type: 'note', text:
            'With no rules, new leads wait for manual assignment. The same screen also sets **lost reasons** and per-stage ' +
            '**response SLAs**.' },
        ],
      },
      {
        id: 'ac-triage',
        heading: 'Triage & QA',
        blocks: [
          { type: 'p', text:
            'Open **Platform → Triage & QA** to review scored calls — a queue with quality scores, sentiment and flags, ' +
            'and a call detail view with a PII-redacted transcript, an audio player and a quality scorecard. From a call ' +
            'you can **Approve & close**, **Escalate**, or **Assign to coach**.' },
          { type: 'p', text:
            'What gets flagged is set in **Settings → Triage rules** (e.g. sentiment below −0.4, quality under 50, a ' +
            'failed tool-call validator flagged as a hallucination, a missed disclaimer, or the customer saying ' +
            '“manager / complaint / lawyer” → escalate immediately). Toggle each rule on or off.' },
        ],
      },
      {
        id: 'ac-hours',
        heading: 'Business hours & compliance',
        blocks: [
          { type: 'p', text:
            'Open **Settings → Business hours** to set the outbound calling/SMS window per weekday. This is a compliance ' +
            'control, not a preference — outbound voice and SMS are **blocked outside these times** (the ACMA window). ' +
            'The **Compliance policies** tab lists the always-on gateway guardrails (DNC honoured, consent required, PII ' +
            'redaction, mandatory disclaimer, Sydney data residency, 7-year audit retention).' },
        ],
      },
      {
        id: 'ac-health',
        heading: 'Onboarding & Dealer Health',
        blocks: [
          { type: 'p', text:
            '**Platform → Onboarding** is the guided go-live checklist (dealership details, dealer-system connection, ' +
            'lead sources, channels, AI persona, trading hours, team, test lead, go live). **Platform → Dealer Health** ' +
            'gives a single 0–100 score with an A–E grade, weighted across lead volume, first-contact speed, appointment ' +
            'show rate and active users — hit **Recompute** for a fresh snapshot.' },
        ],
      },
    ],
  },
];

/** Flattened index for the search box. */
export const SEARCH_INDEX = ARTICLES.flatMap((a) =>
  a.sections.map((s) => ({
    articleId: a.id,
    articleTitle: a.title,
    sectionId: s.id,
    heading: s.heading,
    // Everything renderable, lower-cased once, so search is a cheap includes().
    haystack: [
      a.title, a.blurb, s.heading,
      ...s.blocks.flatMap((b) => [
        b.text || '', b.title || '',
        ...(b.items || []),
        ...(b.head || []),
        ...(b.rows || []).flat(),
      ]),
    ].join(' ').toLowerCase(),
  })));
